import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';

interface Wallet {
  balance: number;
  held: number;
  available: number;
  trustedBidder: boolean;
  transactions: Array<{
    id: string;
    type: string;
    amount: number | string;
    balanceAfter: number | string;
    reason: string | null;
    createdAt: string;
  }>;
}

const TYPE_LABELS: Record<string, { label: string; color: 'green' | 'red' | 'amber' | 'navy' | 'gray' }> = {
  DEPOSIT:       { label: 'إيداع',       color: 'green' },
  WITHDRAWAL:    { label: 'سحب',         color: 'red' },
  HOLD:          { label: 'حجز عربون',   color: 'amber' },
  RELEASE:       { label: 'إلغاء حجز',    color: 'navy' },
  WIN_DEDUCTION: { label: 'خصم فوز',      color: 'red' },
  ADMIN_ADJUST:  { label: 'تعديل إدارة',  color: 'navy' },
  REFUND:        { label: 'استرداد',      color: 'green' },
};

export function MyWalletPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['me', 'wallet'],
    queryFn: () => api.get<ApiSuccess<Wallet>>('/api/me/wallet'),
  });

  if (isLoading || !data?.data) return <div className="text-gray-500">⟳ جاري التحميل…</div>;
  const w = data.data;

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-6">المحفظة الإلكترونية</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="!bg-gradient-to-br from-navy-dark to-navy text-white">
          <CardBody>
            <div className="text-xs opacity-80 mb-1">الرصيد المتاح</div>
            <div className="text-3xl font-black">{w.available.toLocaleString('en-US')}</div>
            <div className="text-xs opacity-70 mt-1">ر.س — للمزايدة</div>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="text-xs text-gray-500 mb-1">الرصيد الإجمالي</div>
            <div className="text-2xl font-black text-navy-dark">{w.balance.toLocaleString('en-US')}</div>
            <div className="text-xs text-gray-400 mt-1">ر.س</div>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="text-xs text-gray-500 mb-1">المحجوز كعربون</div>
            <div className="text-2xl font-black text-amber-600">{w.held.toLocaleString('en-US')}</div>
            <div className="text-xs text-gray-400 mt-1">ر.س</div>
          </CardBody>
        </Card>
      </div>

      {w.trustedBidder && (
        <Card className="mb-6 !bg-amber-50 border-amber-200">
          <CardBody className="flex items-center gap-3">
            <span className="text-3xl">⭐</span>
            <div>
              <div className="font-black text-amber-900">مزايد موثوق</div>
              <div className="text-xs text-amber-800">تم تفعيل المزايدة بدون عربون لكل المزادات.</div>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody className="!p-0">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="font-black text-lg text-navy-dark">📜 سجل المعاملات</h2>
          </div>
          {w.transactions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">لا توجد معاملات بعد</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">النوع</th>
                  <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">المبلغ</th>
                  <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">الرصيد بعد</th>
                  <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">السبب</th>
                  <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">التاريخ</th>
                </tr>
              </thead>
              <tbody>
                {w.transactions.map((t) => {
                  const cfg = TYPE_LABELS[t.type] ?? { label: t.type, color: 'gray' as const };
                  const isPositive = ['DEPOSIT', 'RELEASE', 'REFUND'].includes(t.type);
                  return (
                    <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3"><Badge color={cfg.color}>{cfg.label}</Badge></td>
                      <td className={`px-4 py-3 font-black ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                        {isPositive ? '+' : '-'}{Number(t.amount).toLocaleString('en-US')}
                      </td>
                      <td className="px-4 py-3 font-bold">{Number(t.balanceAfter).toLocaleString('en-US')}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 line-clamp-1">{t.reason ?? '—'}</td>
                      <td className="px-4 py-3 text-xs">{new Date(t.createdAt).toLocaleString('ar-SA')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
